/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, useState } from 'react';
import { Await, redirect, useAsyncError } from 'react-router';
import type { Route } from './+types/_app._index';
import type { ShopperSearch } from '@/scapi';
import { fetchCarouselProducts } from '@/components/product-carousel/loaders';
import heroImage from '/images/hero.jpg';
import jetSetImage from '/images/jet-set.webp';
import manhattanImage from '/images/manhattan.jpg';
import { siteContext, resolvePrefix, type SiteContext } from '@salesforce/storefront-next-runtime/site-context';
import { Region } from '@/components/region';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getLogger } from '@/lib/logger.server';
import { ProductCarouselSkeleton } from '@/components/product-carousel';
// RGBARRY-CUSTOMIZATION: matching skeleton reserves height for the deferred top-sellers carousel (CLS fix)
import { ProductRecommendationSkeleton } from '@/components/product/skeletons';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { ProductTile, ProductTileProvider } from '@/components/product-tile';
import DynamicImageProvider from '@/providers/dynamic-image';
import { SeoMeta } from '@/components/seo-meta';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { useTranslation } from 'react-i18next';
import type { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { Link } from '@/components/link';
import { routeHref, routes } from '@/route-paths';
import { cn } from '@/lib/utils';
import { useInView } from '@/hooks/use-in-view';
import DynamicImage from '@/components/dynamic-image';
import { fetchProductRecommendations } from '@/lib/product/recommendations.server';
import { EINSTEIN_RECOMMENDERS } from '@/lib/product/einstein-recommenders';
import type { Recommendation } from '@/hooks/recommenders/use-recommenders';
import DeferredProductRecommendations from '@/components/product-recommendations/deferred';

export { shouldRevalidate } from '@/lib/revalidation/routes/home';

// RGBARRY-CUSTOMIZATION: classify this route for the GTM data layer's page-view event
// (see src/analytics/route-analytics.ts).
export const handle = {
    analytics: { routeType: 'home' },
};

const CDN_LIB = 'https://www.baggallini.com/on/demandware.static/-/Sites-baggalini-Library/default';
const CDN_IMG =
    'https://www.baggallini.com/dw/image/v2/BJGK_PRD/on/demandware.static/-/Sites-baggalini-Library/default';

// ─── Section: Hero ────────────────────────────────────────────────────────────
function HeroSection() {
    return (
        <div className="relative min-h-[92vh] bg-[#1A1A1A] overflow-hidden">
            <DynamicImage
                src={heroImage}
                alt="Baggallini Pre-Fall Collection — two women walking through a city with stylish handbags"
                priority="high"
                widths={{ base: 640, sm: 768, md: 1024, lg: 1440, xl: 1920 }}
                className="absolute inset-0 w-full h-full"
                imageProps={{ className: 'w-full h-full object-cover object-center' }}
            />
            {/* Layered gradient: subtle vignette top, strong editorial bar bottom-left */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20" />
            <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-transparent" />

            {/* Main editorial block — anchored bottom left */}
            <div className="absolute bottom-0 left-0 right-0 z-10 px-8 sm:px-12 lg:px-16 pb-12 lg:pb-16 max-w-3xl">
                <h1
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="df-hero-h1 text-6xl sm:text-7xl lg:text-[96px] xl:text-[108px] font-light leading-[0.88] tracking-tight text-primary-foreground mb-10">
                    When You&apos;re
                    <br />
                    <em>Prepared,</em>
                    <br />
                    You&apos;re Confident
                </h1>

                <div className="flex flex-col sm:flex-row sm:items-center gap-6">
                    <Link
                        to={routeHref(routes.category, { categoryId: 'root' })}
                        className="df-hero-cta df-btn-shimmer inline-flex items-center gap-3 bg-background text-[#1A1A1A] text-sm tracking-[0.12em] uppercase font-medium px-9 py-4 hover:bg-[#1A1A1A] hover:text-primary-foreground transition-all duration-300 shadow-lg rounded-none border border-primary-foreground shrink-0 self-start">
                        Shop New Arrivals
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                    </Link>
                    <p className="df-hero-body text-sm lg:text-base text-primary-foreground/60 font-light leading-relaxed">
                        Stylish &amp; functional bags designed
                        <br className="hidden sm:block" /> for travel and everyday life.
                    </p>
                </div>
            </div>
        </div>
    );
}

// ─── Section: Category Tiles ──────────────────────────────────────────────────
function CategorySection() {
    const cats = [
        {
            label: 'New Arrivals',
            sub: 'Just In',
            img: `${CDN_IMG}/dwb4649144/fa26/8.4HP/CatNav_Cat1-D.jpg`,
            href: routeHref(routes.category, { categoryId: 'new-arrivals' }),
            badge: true,
        },
        {
            label: 'Crossbody',
            sub: 'Hands-Free Style',
            img: `${CDN_IMG}/dw9be5e7ca/fa26/8.4HP/CatNav_Cat2-D.jpg`,
            href: routeHref(routes.category, { categoryId: 'crossbody-bags' }),
            badge: false,
        },
        {
            label: 'Backpacks',
            sub: 'Carry It All',
            img: `${CDN_IMG}/dwad44fb33/fa26/8.4HP/CatNav_Cat3-D.jpg`,
            href: routeHref(routes.category, { categoryId: 'backpacks' }),
            badge: false,
        },
        {
            label: 'Travel',
            sub: 'Pack Smarter',
            img: `${CDN_IMG}/dwe5d918d9/fa26/8.4HP/CatNav_Cat5-D.jpg`,
            href: routeHref(routes.category, { categoryId: 'travel-bags' }),
            badge: false,
        },
    ];

    const { ref: headingRef, inView: headingInView } = useInView<HTMLDivElement>({ margin: '-80px' });
    const { ref: gridRef, inView: gridInView } = useInView<HTMLDivElement>({ margin: '-80px' });

    return (
        <section className="bg-[#F9F9F9] py-16 lg:py-20">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
                <div ref={headingRef} className={cn('text-center mb-10 df-reveal', headingInView && 'df-reveal--show')}>
                    <p className="text-[11px] tracking-[0.22em] uppercase text-[#8A8A8A] mb-2">Shop by Category</p>
                    <h2
                        style={{ fontFamily: 'var(--font-display)' }}
                        className="text-3xl lg:text-4xl font-light text-[#1A1A1A]">
                        Bags for Every Journey
                    </h2>
                </div>
                <div ref={gridRef} className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                    {cats.map((cat, i) => (
                        <Link
                            key={cat.label}
                            to={cat.href}
                            style={{ transitionDelay: gridInView ? `${i * 75}ms` : '0ms' }}
                            className={cn('group block df-reveal', gridInView && 'df-reveal--show')}>
                            <div className="relative overflow-hidden aspect-[3/4] bg-[#F9F9F9] rounded-ui">
                                <DynamicImage
                                    src={cat.img}
                                    alt={cat.label}
                                    widths={{ base: 400, lg: 360 }}
                                    heights={{ base: 533, lg: 480 }}
                                    className="absolute inset-0 w-full h-full"
                                    imageProps={{
                                        className:
                                            'w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 mix-blend-multiply',
                                    }}
                                />
                                {cat.badge && (
                                    <span className="absolute top-3 left-3 inline-block text-[9px] tracking-[0.18em] uppercase font-semibold bg-background text-[#1A1A1A] px-2 py-0.5 rounded-sm">
                                        New
                                    </span>
                                )}
                            </div>
                            <div className="pt-3">
                                <p
                                    style={{ fontFamily: 'var(--font-display)' }}
                                    className="text-lg font-light text-[#1A1A1A] leading-tight">
                                    {cat.label}
                                </p>
                                <p className="text-[11px] tracking-wider uppercase text-[#8A8A8A] mt-0.5">{cat.sub}</p>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── Section: Editorial split ─────────────────────────────────────────────────
function EditorialSplit() {
    const { ref, inView } = useInView<HTMLDivElement>({ margin: '-100px' });

    return (
        <section className="bg-background py-0">
            <div ref={ref} className="grid lg:grid-cols-2">
                {/* Left panel — Jet Set Crossbody */}
                <div>
                    {/* Image with curtain wipe */}
                    <div className="relative overflow-hidden aspect-[4/3]">
                        <DynamicImage
                            src={jetSetImage}
                            alt=""
                            widths={{ base: 768, lg: 720 }}
                            className="absolute inset-0 w-full h-full"
                            imageProps={{
                                className:
                                    'w-full h-full object-cover transition-transform duration-700 hover:scale-105',
                            }}
                        />
                        {/* Curtain wipes from right */}
                        <div
                            className={cn(
                                'absolute inset-0 bg-[#1A1A1A]/80 df-curtain origin-right',
                                inView && 'df-curtain--open'
                            )}
                        />
                    </div>
                    {/* Text below image */}
                    <div
                        className={cn(
                            'pt-4 px-8 pb-8 lg:pt-5 lg:px-10 lg:pb-10 df-reveal',
                            inView && 'df-reveal--show'
                        )}
                        style={{ transitionDelay: inView ? '500ms' : '0ms' }}>
                        <p className="text-[11px] tracking-[0.2em] uppercase mb-3 text-[#1a1a1a]/60">
                            Anti-Theft Design
                        </p>
                        <h3
                            style={{ fontFamily: 'var(--font-display)' }}
                            className="text-4xl lg:text-5xl font-light leading-tight mb-5 text-[#1a1a1a]">
                            The Jet Set
                            <br />
                            Capsule
                        </h3>
                        <Link
                            to={routeHref(routes.category, { categoryId: 'jet-set' })}
                            className="inline-flex items-center gap-2 text-sm tracking-[0.1em] uppercase font-medium border-b border-[#1a1a1a] pb-0.5 hover:opacity-60 transition-opacity text-[#1a1a1a]">
                            Shop Now
                        </Link>
                    </div>
                </div>

                {/* Right panel — Manhattan Collection */}
                <div>
                    {/* Image with curtain wipe */}
                    <div className="relative overflow-hidden aspect-[4/3]">
                        <DynamicImage
                            src={manhattanImage}
                            alt=""
                            widths={{ base: 768, lg: 720 }}
                            className="absolute inset-0 w-full h-full"
                            imageProps={{
                                className:
                                    'w-full h-full object-cover transition-transform duration-700 hover:scale-105',
                            }}
                        />
                        {/* Curtain wipes from left, staggered */}
                        <div
                            className={cn(
                                'absolute inset-0 bg-[#1A1A1A]/80 df-curtain origin-left',
                                inView && 'df-curtain--open'
                            )}
                            style={{ transitionDelay: inView ? '120ms' : '0ms' }}
                        />
                    </div>
                    {/* Text below image */}
                    <div
                        className={cn(
                            'pt-4 px-8 pb-8 lg:pt-5 lg:px-10 lg:pb-10 df-reveal',
                            inView && 'df-reveal--show'
                        )}
                        style={{ transitionDelay: inView ? '620ms' : '0ms' }}>
                        <p className="text-[11px] tracking-[0.2em] uppercase mb-3 text-[#1a1a1a]/60">Now Trending</p>
                        <h3
                            style={{ fontFamily: 'var(--font-display)' }}
                            className="text-4xl lg:text-5xl font-light leading-tight mb-5 text-[#1a1a1a]">
                            New in
                            <br />
                            Manhattan
                        </h3>
                        <Link
                            to={routeHref(routes.category, { categoryId: 'manhattan' })}
                            className="inline-flex items-center gap-2 text-sm tracking-[0.1em] uppercase font-medium border-b border-[#1a1a1a] pb-0.5 hover:opacity-60 transition-opacity text-[#1a1a1a]">
                            Shop Collection
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ─── Section: Trust pillars ───────────────────────────────────────────────────
function TrustPillars() {
    const { ref, inView } = useInView<HTMLDivElement>({ margin: '-80px' });

    const pillars = [
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                </svg>
            ),
            title: 'Anti-Theft Built In',
            body: 'Select styles feature slash-resistant straps, locking zippers, and RFID-blocking pockets so you can travel worry-free.',
        },
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
                    <polyline points="17 6 23 6 23 12" />
                </svg>
            ),
            title: 'Free Shipping on $100+',
            body: 'Enjoy free standard shipping on all U.S. orders over $100. Fast fulfillment so your bag arrives when you need it.',
        },
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 .49-4.95" />
                </svg>
            ),
            title: 'Easy 30-Day Returns',
            body: 'Not the right fit? Return unused items within 30 days for a full refund. Hassle-free, no questions asked.',
        },
        {
            icon: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                </svg>
            ),
            title: 'Built to Last',
            body: 'Many customers carry the same Baggallini bag for four or more years. Lightweight materials built for the long haul.',
        },
    ];

    return (
        <section className="bg-background py-14 lg:py-20 border-b border-[#E8E8E8]">
            <div className="max-w-[1280px] mx-auto px-6 lg:px-8">
                <div ref={ref} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
                    {pillars.map((p, i) => (
                        <div
                            key={p.title}
                            style={{ transitionDelay: inView ? `${i * 75}ms` : '0ms' }}
                            className={cn(
                                'flex flex-col items-center text-center gap-4 df-reveal',
                                inView && 'df-reveal--show'
                            )}>
                            <div className="text-[#1A1A1A]">{p.icon}</div>
                            <div>
                                <p className="text-[13px] font-semibold tracking-wide text-[#1A1A1A] mb-2">{p.title}</p>
                                <p className="text-sm text-[#6B6B6B] leading-relaxed">{p.body}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── Section: SEO copy ────────────────────────────────────────────────────────
function SeoBlock() {
    const { ref, inView } = useInView<HTMLElement>({ margin: '-80px' });
    return (
        <section
            ref={ref}
            className={cn(
                'bg-background py-14 lg:py-20 border-t border-[#E8E8E8] df-reveal',
                inView && 'df-reveal--show'
            )}>
            <div className="max-w-[880px] mx-auto px-6 lg:px-8">
                <h2
                    style={{ fontFamily: 'var(--font-display)' }}
                    className="text-3xl lg:text-4xl font-light text-[#1A1A1A] mb-6 text-center">
                    Organized Bags That Simplify Life
                </h2>
                <div className="prose prose-sm max-w-none text-[#6B6B6B] leading-relaxed space-y-4 text-center">
                    <p>
                        Baggallini creates organized bags designed to help women move through the world feeling{' '}
                        <strong className="text-[#1A1A1A] font-medium">confident and in control</strong>. Every bag is
                        engineered with smart compartments, lightweight materials, and versatile silhouettes built for
                        everyday functionality — whether you&apos;re commuting, traveling, or exploring a new city.
                    </p>
                    <p>
                        From our iconic{' '}
                        <Link
                            to={routeHref(routes.category, { categoryId: 'crossbody-bags' })}
                            className="text-[#1A1A1A] underline underline-offset-2 hover:no-underline">
                            crossbody bags
                        </Link>{' '}
                        and{' '}
                        <Link
                            to={routeHref(routes.category, { categoryId: 'backpacks' })}
                            className="text-[#1A1A1A] underline underline-offset-2 hover:no-underline">
                            backpacks
                        </Link>{' '}
                        to our{' '}
                        <Link
                            to={routeHref(routes.category, { categoryId: 'travel-bags' })}
                            className="text-[#1A1A1A] underline underline-offset-2 hover:no-underline">
                            travel bags
                        </Link>{' '}
                        and anti-theft Securtex® collection, every Baggallini style is built around a simple belief:
                        when you&apos;re organized, you&apos;re ready for anything.
                    </p>
                    <p className="text-xs tracking-wide text-[#AAAAAA] uppercase pt-2">
                        Free shipping on orders $100+ · Easy 30-day returns · Anti-Theft styles available
                    </p>
                </div>
            </div>
        </section>
    );
}

// ─── Section: Repeat Offenders ────────────────────────────────────────────────
const repeatOffendersImageWidths = { base: '280px', md: '300px' };

function RepeatOffendersSection({ searchResult }: { searchResult: ShopperSearch.schemas['ProductSearchResult'] }) {
    const products = searchResult?.hits?.slice(0, 8) ?? [];
    return (
        <section className="bg-background py-12 px-6 lg:px-12">
            <Carousel opts={{ align: 'start' }} aria-label="The Repeat Offenders — bestselling bags">
                <CarouselContent className="flex items-stretch -ml-0">
                    {/* Left editorial panel — fixed-width first slide */}
                    <CarouselItem className="basis-[260px] shrink-0 pl-8 md:pl-12 lg:pl-16 flex flex-col justify-center">
                        <h2
                            className="text-[2.5rem] leading-[1.1] font-light text-[#1a1a1a] mb-4"
                            style={{ fontFamily: 'var(--font-display, Georgia, serif)' }}>
                            The Repeat
                            <br />
                            Offenders
                        </h2>
                        <p className="text-sm text-[#5a5a5a] leading-relaxed mb-6 max-w-[220px]">
                            The bestselling bags customers carry everywhere — then come back for in every color.
                        </p>
                        <Link
                            to={routeHref(routes.category, { categoryId: 'new-arrivals' })}
                            className="inline-flex items-center justify-center border border-[#1a1a1a] text-[#1a1a1a] text-[11px] font-bold tracking-[0.12em] uppercase px-6 py-3 hover:bg-[#1a1a1a] hover:text-primary-foreground transition-colors w-fit">
                            Shop Bestsellers
                        </Link>
                    </CarouselItem>

                    {/* Product tiles */}
                    <ProductTileProvider>
                        <DynamicImageProvider value={{ widths: repeatOffendersImageWidths }}>
                            {products.map((product) => (
                                <CarouselItem
                                    key={product.productId}
                                    className="basis-[260px] md:basis-[300px] shrink-0 pl-4 flex">
                                    <div className="w-full min-w-0 flex">
                                        <ProductTile product={product} className="h-full w-full" />
                                    </div>
                                </CarouselItem>
                            ))}
                        </DynamicImageProvider>
                    </ProductTileProvider>
                </CarouselContent>
                <CarouselPrevious className="flex left-2 -translate-x-0 size-9 shadow-md disabled:hidden" />
                <CarouselNext className="flex right-2 translate-x-0 size-9 shadow-md" />
            </Carousel>
        </section>
    );
}

// ─── Page Designer metadata ───────────────────────────────────────────────────
@PageType({
    name: 'Home Page',
    description: 'Main landing page with hero, category tiles, featured products, and brand content',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'main',
        name: 'Page Content',
        description: 'Full-page content region — place any blocks in any order',
        maxComponents: 15,
    },
])
export class HomePageMetadata {}

function FeaturedProductsError() {
    const error = useAsyncError() as NormalizedApiError;
    const { t } = useTranslation('home');
    return (
        <div role="alert" className="py-8 text-center text-muted-foreground">
            <p>{t('featuredProducts.loadFailed')}</p>
            {import.meta.env.DEV && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status}</span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

export type HomePageData = {
    // RGBARRY-CUSTOMIZATION: page is awaited (not deferred) so PD components SSR and DynamicImage can preload LCP image
    page: Awaited<ReturnType<typeof fetchPageWithComponentData>>;
    searchResult: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    // RGBARRY-CUSTOMIZATION: homepage Einstein top-sellers — deferred so it doesn't block LCP
    homeRecommendationsPromise?: Promise<Recommendation>;
    pageUrl: string;
    ogImageUrl: string;
};

export async function loader(args: Route.LoaderArgs): Promise<HomePageData> {
    const logger = getLogger(args.context);
    logger.debug('HomePage: loader starting');

    const config = getConfig(args.context);
    const requestUrl = new URL(args.request.url);

    if (requestUrl.pathname === '/' && config.url?.prefix && config.url.prefix !== '/') {
        const siteRef = config.siteAliasMap?.[config.defaultSiteId] ?? config.defaultSiteId;
        const defaultSite = config.commerce.sites.find((s) => s.id === config.defaultSiteId);
        const defaultLocale = defaultSite?.defaultLocale ?? config.i18n.fallbackLng;
        const localeRef = config.localeAliasMap?.[defaultLocale] ?? defaultLocale;
        const prefixedPath = resolvePrefix({
            prefix: config.url.prefix,
            params: { siteId: siteRef, localeId: localeRef },
        });
        throw redirect(`${prefixedPath}/`);
    }

    const currency = (args.context.get(siteContext) as SiteContext).currency;
    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);
    const ogImageUrl = `${CDN_LIB}/dw378c2f06/fa26/7.7HP/BG-20260706-HERO-M.jpg`;

    // Await the PD page so above-fold components SSR in the initial HTML flush.
    // searchResult and homeRecommendationsPromise stay deferred — below the fold, non-critical for LCP.
    const page = await fetchPageWithComponentData(args, { pageId: 'homepage' });

    return {
        page,
        searchResult: fetchCarouselProducts(args.context, {
            categoryId: 'root',
            limit: 8,
            currency: currency ?? undefined,
        }),
        // RGBARRY-CUSTOMIZATION: Einstein "Trending Now" carousel for homepage — identity-only, no anchor
        // product. Uses the SFRA homepage recommender (products-in-all-categories) via MOST_VIEWED.
        homeRecommendationsPromise: fetchProductRecommendations(
            { context: args.context, request: args.request },
            { name: EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_MOST_VIEWED, ...(currency ? { currency } : {}) }
        ),
        pageUrl,
        ogImageUrl,
    };
}

export default function HomePage({ loaderData }: { loaderData: HomePageData }) {
    // RGBARRY-CUSTOMIZATION: pin promise so revalidation doesn't re-suspend the carousel
    const [pinnedHomeRecs] = useState(() => loaderData.homeRecommendationsPromise ?? Promise.resolve({}));
    const { t } = useTranslation('product');
    // RGBARRY-CUSTOMIZATION: shared between the resolved carousel and its skeleton fallback so the
    // reserved-space title matches the resolved title exactly (CLS fix, see fallback prop below)
    const topSellersTitle = t('recommendations.trendingNow', { defaultValue: 'Trending Now' });

    return (
        <div className="pb-16 -mt-8">
            <SeoMeta
                rawTitle
                title="Baggallini® — Stylish & Functional Bags for Travel & Everyday"
                description="Shop Baggallini crossbody bags, backpacks, travel bags, and anti-theft styles. Organized bags built for women on the go. Free shipping on $100+."
                openGraph={{
                    type: 'website',
                    url: loaderData.pageUrl,
                    image: loaderData.ogImageUrl,
                }}
            />

            {/* Single Page Designer region — full editorial freedom, full fallback if PD unavailable */}
            <Region
                page={loaderData.page}
                regionId="main"
                fallbackElement={<div className="h-[87.5vh] bg-muted animate-pulse" />}
                errorElement={
                    <>
                        <HeroSection />
                        <CategorySection />
                        <EditorialSplit />
                        {/* Repeat Offenders — editorial bestsellers carousel */}
                        <Suspense fallback={<ProductCarouselSkeleton title="The Repeat Offenders" />}>
                            <Await resolve={loaderData.searchResult} errorElement={<FeaturedProductsError />}>
                                {(searchResult) => <RepeatOffendersSection searchResult={searchResult} />}
                            </Await>
                        </Suspense>
                        <TrustPillars />
                        <SeoBlock />
                    </>
                }
            />

            {/* RGBARRY-CUSTOMIZATION: Einstein top-sellers carousel — below PD content, deferred */}
            <div className="section-container mt-16">
                <DeferredProductRecommendations
                    recommenderName={EINSTEIN_RECOMMENDERS.EMPTY_SEARCH_RESULTS_MOST_VIEWED}
                    recommenderTitle={topSellersTitle}
                    data={pinnedHomeRecs}
                    className="max-w-none px-0"
                    // RGBARRY-CUSTOMIZATION: reserve the carousel's final height (CLS fix) — without this,
                    // both the pre-idle phase and the pending-Suspense phase render nothing (fallback
                    // defaults to null), so the whole block pops in from 0 height once the deferred
                    // recommendation promise resolves, shoving the footer down.
                    fallback={<ProductRecommendationSkeleton title={topSellersTitle} className="max-w-none px-0" />}
                />
            </div>
        </div>
    );
}
